"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { Icon } from "@/components/ui/Icon";
import Image from "next/image";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import type { Locale } from "@/config/i18n";
import { CHAUFFEUR_SLUG_UI } from "./chauffeurSlugUi";
import { tr } from "@/config/localeText";

type HeroProps = {
  title: string;
  subtitle?: string;
  keywords?: string[];
  imageSrc?: string;
  imageAlt?: string;
  badges?: string[];
  ctaPrimaryHref?: string;
  ctaPrimaryLabel?: string;
  ctaSecondaryHref?: string;
  ctaSecondaryLabel?: string;
  breadcrumbs?: { label: string; href?: string }[];
  locale?: Locale;
};

export const HeroSection = ({
  title,
  subtitle,
  keywords,
  imageSrc,
  imageAlt,
  badges,
  ctaPrimaryHref = "/reservation",
  ctaPrimaryLabel = "Réserver",
  ctaSecondaryHref = "/contact",
  ctaSecondaryLabel = "Demander un devis",
  breadcrumbs,
  locale = "fr",
}: HeroProps): JSX.Element => {
  const ui = tr(CHAUFFEUR_SLUG_UI, locale);
  return (
    <section className="relative isolate overflow-hidden bg-white text-black">
      <div className="mx-auto flex min-h-[70vh] max-w-[90rem] flex-col justify-center px-6 py-20 md:px-8 lg:min-h-[76vh]">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7 xl:col-span-7">
            {breadcrumbs && breadcrumbs.length > 0 && (
              <Breadcrumbs
                items={breadcrumbs}
                className="mb-5 text-black/60 [&_a:hover]:text-primary"
              />
            )}
            <motion.h1
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8 font-space-age text-[1.9rem] leading-tight tracking-[0.16em] md:text-4xl lg:text-5xl"
            >
              {title}
            </motion.h1>
            {subtitle && (
              <motion.p
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.05 }}
                className="max-w-2xl text-base text-black/75 md:text-lg"
              >
                {subtitle}
              </motion.p>
            )}

            {(() => {
              const combined = Array.from(
                new Set([...(badges || []), ...(keywords || [])]),
              );
              if (combined.length === 0) return null;
              return (
                <motion.div
                  initial={false}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.12 }}
                  className="mt-5 flex flex-wrap gap-2"
                >
                  {combined.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-black/15 bg-black/[0.03] px-3 py-1 text-xs text-black/80"
                    >
                      {tag}
                    </span>
                  ))}
                </motion.div>
              );
            })()}

            <motion.div
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.18 }}
              className="mt-6 flex flex-wrap items-center gap-3"
            >
              <Link
                href={ctaPrimaryHref}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-black transition-colors hover:bg-gold-600 hover:text-white"
              >
                <Icon icon="mdi:calendar-check" className="h-4 w-4" />
                {ctaPrimaryLabel}
              </Link>
              <Link
                href={ctaSecondaryHref}
                className="inline-flex items-center gap-2 rounded-lg border border-black/15 px-5 py-2 text-sm font-semibold text-black/90 transition-colors hover:border-primary"
              >
                <Icon icon="mdi:car" className="h-4 w-4" />
                {ctaSecondaryLabel}
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-5 xl:col-span-5">
            <div className="relative overflow-hidden rounded-2xl border border-black/10 bg-black/[0.02]">
              {imageSrc ? (
                <div className="relative h-64 w-full md:h-80 lg:h-[30rem]">
                  <Image
                    src={imageSrc}
                    alt={imageAlt || title}
                    fill
                    priority
                    fetchPriority="high"
                    className="object-cover opacity-95"
                    sizes="(min-width: 1024px) 35vw, 100vw"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                </div>
              ) : (
                <div className="p-5">
                  <div className="mb-3 text-xs uppercase tracking-[0.25em] text-black/60">
                    Pourquoi nous choisir
                  </div>
                  <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <li className="flex items-center gap-2 text-sm text-black/80">
                      <Icon
                        icon="mdi:clock-check"
                        className="h-4 w-4 text-primary"
                      />
                      Ponctualité 24/7
                    </li>
                    <li className="flex items-center gap-2 text-sm text-black/80">
                      <Icon icon="mdi:star" className="h-4 w-4 text-primary" />
                      VTC premium
                    </li>
                    <li className="flex items-center gap-2 text-sm text-black/80">
                      <Icon
                        icon="mdi:map-marker"
                        className="h-4 w-4 text-primary"
                      />
                      Nice • Cannes • Monaco
                    </li>
                    <li className="flex items-center gap-2 text-sm text-black/80">
                      <Icon
                        icon="mdi:shield-check"
                        className="h-4 w-4 text-primary"
                      />
                      Tarifs clairs
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="sr-only">
          <h2>{ui.srTitle}</h2>
          <p>{ui.srText}</p>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
